// Shared presentation helpers for the reply template surfaces (Session AUA, email system Phase 3): the Templates page, a
// template's own page and the new template form. One home for how a status, a proposal kind, a usage figure and a slot in
// the wording read, so the list and the detail cannot disagree. Plain English throughout (never a raw key).
import type { ReactNode } from "react";
import type {
  ReplyTemplateCheckFailure,
  ReplyTemplateProposalKind,
  ReplyTemplateProposalStatus,
  ReplyTemplateSender,
  ReplyTemplateStatus,
  ReplyTemplateUsage,
} from "../../lib/api";

export const TEMPLATE_STATUS_LABEL: Record<ReplyTemplateStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  retired: "Retired",
};

export const PROPOSAL_KIND_LABEL: Record<ReplyTemplateProposalKind, string> = {
  new_template: "New template",
  revision: "Revision of a template",
  flagged_revision: "Revision of a flagged template",
};

export const PROPOSAL_STATUS_LABEL: Record<ReplyTemplateProposalStatus, string> = {
  pending: "Waiting for a decision",
  accepted: "Accepted",
  rejected: "Rejected",
};

export const SENDER_LABEL: Record<ReplyTemplateSender, string> = {
  verified_customer: "a verified customer",
  new_enquirer: "a new enquirer",
};

export const ORIGIN_LABEL: Record<string, string> = {
  manual: "Written by the office",
  proposal: "From a proposal",
  import: "Imported",
};

/** The documents a template can attach (the server's ATTACHMENT_KEYS; only the Schedule of Fees exists). */
export const TEMPLATE_ATTACHMENTS: { key: string; label: string }[] = [{ key: "scheduleOfFees", label: "Schedule of Fees" }];

export const BESPOKE_MARKER = "{bespoke}";

/** A fraction (0.75) as a whole percentage ("75%"), or a dash when nothing is settled yet. */
export function percent(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "-";
  return `${Math.round(rate * 100)}%`;
}

/** The usage figures as one plain sentence. */
export function usageSentence(u: ReplyTemplateUsage): string {
  if (u.drafts === 0) return "Not used for a draft yet.";
  return `${u.drafts} draft${u.drafts === 1 ? "" : "s"}: ${u.unchanged} sent unchanged, ${u.smallEdits} with small edits, ${u.heavilyRewritten} rewritten, ${u.discarded} discarded, ${u.notYetSent} not yet sent.`;
}

// The same placeholder shape the server's slot registry reads ("{customer.name}", "{bespoke}").
const PLACEHOLDER_RE = /(\{[a-z][A-Za-z0-9]*(?:\.[a-zA-Z][A-Za-z0-9]*)*\})/g;

/**
 * The wording with every slot visibly marked: each "{slot}" becomes a chip, and "{bespoke}" a chip saying where the one
 * bespoke sentence goes. Everything else is shown exactly as written (whitespace kept by the caller's pre-wrap).
 */
export function WordingWithSlots({ body, labels }: { body: string; labels?: Record<string, string> }): ReactNode {
  const parts = body.split(PLACEHOLDER_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return <span key={i}>{part}</span>;
        const name = part.slice(1, -1);
        const isBespoke = part === BESPOKE_MARKER;
        return (
          <mark
            key={i}
            data-slot={name}
            title={isBespoke ? "The one sentence the engine may add for anything specific in the message" : (labels?.[name] ?? name)}
            className={`rounded px-1 font-menu text-xs ${isBespoke ? "bg-ele-amber-wash text-ele-wash-ink" : "bg-ele-panel-tint text-ele-wash-blue-ink"}`}
          >
            {isBespoke ? "[bespoke sentence]" : part}
          </mark>
        );
      })}
    </>
  );
}

/** Every check failure, each with the line it is about. */
export function CheckFailures({ failures, heading }: { failures: Array<Pick<ReplyTemplateCheckFailure, "field" | "detail" | "line" | "check">>; heading?: string }) {
  if (failures.length === 0) return null;
  return (
    <div data-testid="check-failures">
      <p className="text-sm font-semibold text-ele-danger">{heading ?? `${failures.length} check${failures.length === 1 ? "" : "s"} failed:`}</p>
      <ul className="mt-2 space-y-2">
        {failures.map((f, i) => (
          <li key={`${f.field}-${i}`} className="text-sm">
            <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{FIELD_LABEL[f.field] ?? f.field}</span>
            {f.line ? (
              <blockquote className="mt-0.5 border-y border-ele-hairline py-1">
                <mark className="rounded bg-ele-amber-wash px-1 text-ele-wash-ink">{f.line}</mark>
              </blockquote>
            ) : null}
            <p className="mt-0.5 text-ele-body">{f.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const FIELD_LABEL: Record<string, string> = {
  key: "Key",
  category: "Kind of email",
  title: "Title",
  whenToUse: "When to use",
  whenNotToUse: "When not to use",
  body: "Wording",
  requiredSlots: "Slots",
  attachments: "Attachments",
  allowBespokeSentence: "Bespoke sentence",
  document: "The document",
};
